import { Button } from "@/components/ui/button";
import {
  Dialog,
  DialogContent,
  DialogDescription,
  DialogFooter,
  DialogHeader,
  DialogTitle
} from "@/components/ui/dialog";
import { Input } from "@/components/ui/input";
import { Skeleton } from "@/components/ui/skeleton";
import { analytics } from "@/lib/analytics";
import { handleActionError } from "@/lib/errors";
import {
  useAddSavedListItems,
  useCreateSavedList,
  useSavedLists
} from "@/queries/customer/saved-lists";
import { toast } from "@medusajs/ui";
import type { SalonStockerHttpTypes } from "@repo/api-sdk";
import { TruncateTooltip } from "@repo/design-system";
import { ChevronRight, ListPlus, Plus } from "lucide-react";
import { memo, useCallback, useState, type FormEvent } from "react";

type Item = SalonStockerHttpTypes.StoreAddSavedListItem;

const plur = (n: number, word: string) => `${n} ${n === 1 ? word : `${word}s`}`;

interface AddToSavedListButtonProps {
  /** The `(variant_id, seller_id, quantity)` lines to add (one per variant). */
  items: Item[];
  disabled?: boolean;
  className?: string;
  label?: string;
}

/**
 * "Add to list" (F3) - adds the given variant lines to a saved list chosen from
 * a picker, or to a new list created inline (seeded with the items). Operates at
 * variant level: `items` carries one `(variant_id, seller_id, quantity)` per
 * selected variant, so it works for single- and multi-variant products alike.
 */
export const AddToSavedListButton = memo(function AddToSavedListButton({
  items,
  disabled = false,
  className,
  label = "Add to list"
}: AddToSavedListButtonProps) {
  const [open, setOpen] = useState(false);
  const [mode, setMode] = useState<"pick" | "create">("pick");
  const [name, setName] = useState("");
  const [error, setError] = useState<string | null>(null);

  const lists = useSavedLists({ enabled: open });
  const addItems = useAddSavedListItems();
  const createList = useCreateSavedList();

  const itemCount = items.length;
  const busy = addItems.isPending || createList.isPending;

  const reset = useCallback(() => {
    setMode("pick");
    setName("");
    setError(null);
  }, []);

  const handleOpenChange = useCallback(
    (next: boolean) => {
      setOpen(next);
      if (!next) reset();
    },
    [reset]
  );

  const addToExisting = useCallback(
    async (listId: string, listName: string) => {
      if (!items.length) return;
      setError(null);
      try {
        await addItems.mutateAsync({ listId, items });
        analytics.savedListItemsAdded({
          list_id: listId,
          item_count: items.length,
          source: "product"
        });
        setOpen(false);
        reset();
        toast.success(`Added to "${listName}"`, {
          description: `${plur(items.length, "item")} added.`
        });
      } catch (err) {
        setError(
          handleActionError(err, {
            boundary: "product saved list add items",
            fallback:
              "We couldn't add these items to your list. Please try again."
          })
        );
      }
    },
    [items, addItems, reset]
  );

  const handleCreate = useCallback(
    async (event: FormEvent) => {
      event.preventDefault();
      const trimmed = name.trim();
      if (!trimmed || !items.length) return;
      setError(null);
      try {
        const { saved_list } = await createList.mutateAsync({
          name: trimmed,
          items
        });
        analytics.savedListCreated({
          list_id: saved_list.id,
          source: "scratch",
          item_count: saved_list.items.length
        });
        setOpen(false);
        reset();
        toast.success(`"${saved_list.name}" created`, {
          description: `${plur(items.length, "item")} added.`
        });
      } catch (err) {
        setError(
          handleActionError(err, {
            boundary: "product saved list create",
            fallback: "We couldn't create your list. Please try again."
          })
        );
      }
    },
    [name, items, createList, reset]
  );

  const existing = lists.data?.saved_lists ?? [];

  return (
    <>
      <Button
        type="button"
        variant="outline"
        className={className}
        onClick={() => setOpen(true)}
        disabled={disabled}
      >
        <ListPlus className="h-4 w-4" aria-hidden="true" />
        {label}
      </Button>

      <Dialog open={open} onOpenChange={handleOpenChange}>
        <DialogContent className="sm:max-w-md">
          {mode === "pick" ? (
            <>
              <DialogHeader>
                <DialogTitle>Add to a saved list</DialogTitle>
                <DialogDescription>
                  {plur(itemCount, "item")} to add.
                </DialogDescription>
              </DialogHeader>

              <div className="max-h-[45vh] space-y-1 overflow-y-auto py-2">
                {lists.isLoading ? (
                  [0, 1].map((i) => (
                    <Skeleton key={i} className="h-11 w-full" />
                  ))
                ) : existing.length === 0 ? (
                  <p className="px-1 py-6 text-center text-sm text-gray-500 dark:text-gray-400">
                    You don&apos;t have any lists yet. Create one below.
                  </p>
                ) : (
                  existing.map((list) => (
                    <button
                      key={list.id}
                      type="button"
                      disabled={busy}
                      onClick={() => addToExisting(list.id, list.name)}
                      className="border-border flex w-full items-center justify-between gap-3 rounded-md border px-3 py-2 text-left transition-colors hover:border-purple-300 hover:bg-purple-50/50 focus:outline-none focus:ring-2 focus:ring-purple-600 disabled:opacity-50 dark:hover:bg-purple-900/10"
                    >
                      <span className="min-w-0">
                        <TruncateTooltip
                          as="span"
                          className="block text-sm font-medium text-gray-900 dark:text-white"
                        >
                          {list.name}
                        </TruncateTooltip>
                        <span className="text-xs text-gray-500 dark:text-gray-400">
                          {plur(list.item_count, "item")}
                        </span>
                      </span>
                      <ChevronRight
                        className="h-4 w-4 flex-shrink-0 text-gray-400"
                        aria-hidden="true"
                      />
                    </button>
                  ))
                )}
              </div>

              {error ? (
                <p
                  role="alert"
                  className="text-sm text-red-600 dark:text-red-400"
                >
                  {error}
                </p>
              ) : null}

              <DialogFooter>
                <Button
                  type="button"
                  variant="outline"
                  onClick={() => {
                    setMode("create");
                    setError(null);
                  }}
                  disabled={busy}
                >
                  <Plus className="mr-2 h-4 w-4" aria-hidden="true" />
                  Create new list
                </Button>
              </DialogFooter>
            </>
          ) : (
            <form onSubmit={handleCreate}>
              <DialogHeader>
                <DialogTitle>Create a saved list</DialogTitle>
                <DialogDescription>
                  {plur(itemCount, "item")} will be added.
                </DialogDescription>
              </DialogHeader>

              <div className="py-4">
                <Input
                  value={name}
                  onChange={(event) => setName(event.target.value)}
                  placeholder="List name"
                  aria-label="List name"
                  maxLength={100}
                  autoFocus
                />
                {error ? (
                  <p
                    role="alert"
                    className="mt-2 text-sm text-red-600 dark:text-red-400"
                  >
                    {error}
                  </p>
                ) : null}
              </div>

              <DialogFooter className="gap-2">
                <Button
                  type="button"
                  variant="outline"
                  onClick={() => {
                    setMode("pick");
                    setError(null);
                  }}
                  disabled={busy}
                >
                  Back
                </Button>
                <Button type="submit" disabled={!name.trim() || busy}>
                  {busy ? "Saving…" : "Create & add"}
                </Button>
              </DialogFooter>
            </form>
          )}
        </DialogContent>
      </Dialog>
    </>
  );
});
